/*
  The table shell (§6.2, §6.3). New name, new contract: it replaces the four old shared
  parts — a sortable head, a pagination wrapper, a generated-cell row and a no-results row
  — which task 11 deleted once this had taken their last consumer.

  It is presentational on purpose. Rows arrive already filtered, sorted and paginated, so
  the view composes that pipeline in one visible place and task 6b's filter cannot end up
  applied after pagination. sortRowsByColumn() in ./columns is the sort half of it.

  Two layouts, one descriptor list:
    md and up   a real <table> inside its own scroll container, sticky header
    below md    a list of cards, with the sort control moved outside (§6.3)
*/

import { ReactNode } from "react";

import DataTableHead from "./DataTableHead";
import DataTableStackedList from "./DataTableStackedList";
import StackedSortControl from "./StackedSortControl";
import {
  DataTableEmpty,
  DataTableError,
  DataTableSkeletonCards,
  DataTableSkeletonRows,
} from "./DataTableStates";
import { columnAlignClass, renderCellContent } from "./columns";
import { resolveTableStatus } from "./tableState";
import {
  TABLE_BASE,
  TABLE_BODY_CELL,
  TABLE_CELL_PADDING,
  TABLE_CELL_PADDING_TIGHT,
  TABLE_ROW_FOCUS,
  TABLE_ROW_HOVER,
  TABLE_ROW_ZEBRA,
  TABLE_SCROLL_AREA,
  TABLE_SCROLL_AREA_MAX_HEIGHT,
} from "./tableStyles";
import { useIsStacked } from "./useIsStacked";
import { DataTableColumn, TableSortState } from "./types";

export interface DataTableProps<TRow> {
  columns: readonly DataTableColumn<TRow>[];
  /* Already filtered, sorted and paginated by the view. */
  rows: readonly TRow[];
  getRowKey: (row: TRow, index: number) => string | number;
  /*
    The table's accessible name. Rendered as a <caption>, sr-only unless captionVisible.
    It also names the scroll region and the stacked list.
  */
  caption: string;
  captionVisible?: boolean;
  sort?: TableSortState;
  /* Omit for a table with no sort affordance anywhere — D19's SearchCardsRowTable. */
  onSortChange?: (sort: TableSortState) => void;
  /*
    The three states §6.2 requires. isLoading paints a skeleton the shape of the table,
    error paints a reachable error — the RTK Query error the views discard today
    (GameView.tsx:48, CardView.tsx:27) is what belongs here — and no rows paints the empty
    state. resolveTableStatus() picks between them; loading wins over a stale error.
  */
  isLoading?: boolean;
  error?: unknown;
  /* Shown in place of the rows when there are none. */
  emptyState?: ReactNode;
  /* Shown in place of the rows when error is set. Pass one to wire a retry. */
  errorState?: ReactNode;
  /* Placeholder row count. Set it to the page size and the table never resizes. */
  skeletonRows?: number;
  /*
    The scroll container's height cap. A sticky header only looks sticky while the body
    can scroll under it, so there is a default; pass "" for a table that should grow to
    its full height instead.
  */
  scrollAreaClassName?: string;
  className?: string;
}

const NO_SORT: TableSortState = { orderBy: "", order: "asc" };

function DataTable<TRow>(props: DataTableProps<TRow>) {
  const {
    columns,
    rows,
    getRowKey,
    caption,
    captionVisible = false,
    sort = NO_SORT,
    onSortChange,
    isLoading = false,
    error,
    emptyState = <DataTableEmpty />,
    errorState = <DataTableError />,
    skeletonRows,
    scrollAreaClassName = TABLE_SCROLL_AREA_MAX_HEIGHT,
    className = "",
  } = props;

  const stacked = useIsStacked();
  const status = resolveTableStatus(isLoading, error, rows.length);

  /*
    The skeleton is aria-hidden, so this is what a screen reader gets instead. It is
    rendered whatever the state is (empty while there is nothing to say) so the live
    region exists before it has content — one created at the moment it fills is not
    reliably announced.
  */
  const liveStatus = (
    <p role="status" className="sr-only">
      {status === "loading" ? `Loading ${caption}` : ""}
    </p>
  );

  if (stacked) {
    return (
      <div className={`flex flex-col gap-3 ${className}`}>
        <h2 className={captionVisible ? "text-base font-semibold" : "sr-only"}>
          {caption}
        </h2>
        {liveStatus}
        {onSortChange && status === "rows" ? (
          <StackedSortControl
            columns={columns}
            sort={sort}
            onSortChange={onSortChange}
          />
        ) : null}
        {status === "loading" ? (
          <DataTableSkeletonCards columns={columns} rows={skeletonRows} />
        ) : null}
        {status === "error" ? errorState : null}
        {status === "empty" ? emptyState : null}
        {status === "rows" ? (
          <DataTableStackedList
            columns={columns}
            rows={rows}
            getRowKey={getRowKey}
            label={caption}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div
      /*
        role/tabIndex so the scroll container is reachable and scrollable by keyboard —
        a scrollable region that only a mouse can move is a WCAG failure.
      */
      role="region"
      aria-label={caption}
      tabIndex={0}
      className={`${TABLE_SCROLL_AREA} ${scrollAreaClassName} ${className}`}
    >
      {liveStatus}
      <table className={TABLE_BASE}>
        <caption
          className={
            captionVisible
              ? `${TABLE_CELL_PADDING} text-left text-base font-semibold text-pcg-text`
              : "sr-only"
          }
        >
          {caption}
        </caption>
        {/*
          The head renders in every state, including the skeleton: it is what gives the
          placeholder rows the column widths the real rows will have, so nothing jumps
          when the data lands. Its sort controls go quiet while there is nothing to sort.
        */}
        <DataTableHead
          columns={columns}
          sort={sort}
          onSortChange={status === "rows" ? onSortChange : undefined}
        />
        <tbody>
          {status === "loading" ? (
            <DataTableSkeletonRows columns={columns} rows={skeletonRows} />
          ) : null}
          {status === "error" || status === "empty" ? (
            <tr>
              <td colSpan={columns.length} className={TABLE_BODY_CELL}>
                {status === "error" ? errorState : emptyState}
              </td>
            </tr>
          ) : null}
          {status === "rows" &&
            rows.map((row, index) => (
              <tr
                key={getRowKey(row, index)}
                className={`${TABLE_ROW_ZEBRA} ${TABLE_ROW_HOVER} ${TABLE_ROW_FOCUS}`}
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={`${TABLE_BODY_CELL} ${columnAlignClass(column)} ${
                      column.compact ? TABLE_CELL_PADDING_TIGHT : ""
                    } ${column.className ?? ""}`}
                  >
                    {renderCellContent(column, row)}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
